<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
// NOTICE: KAPTION-INTEGRATION — agent-presence.VISIBILITY.3
import KaptionLivePresenceNotice from './KaptionLivePresenceNotice.vue';
import { useKaptionLivePresence } from 'dashboard/composables/useKaptionLivePresence';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');
const { canUseFeature } = useFeaturePermissions();
// NOTICE: KAPTION-INTEGRATION — agent-presence.VISIBILITY.1
const { isLivePresenceLost, markPresenceRefreshed } = useKaptionLivePresence();

const { isImpersonating } = useImpersonation();

// NOTICE: KAPTION-INTEGRATION
// When the current user is inside the client-managed enforcement scope, live
// presence decides whether they are online and the opt-out disappears.
// feature-permissions.CONVERSATION.13 — inverted semantics: permission means
// presence enforcement applies, so the opt-out is hidden.
const hideAutoOfflineToggle = computed(() =>
  canUseFeature('enforce_auto_offline')
);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
    // NOTICE: KAPTION-INTEGRATION — agent-presence.REFRESH.2
    // The request also refreshes server-side presence now. Remember when, so
    // the notice can say the refresh is temporary while the heartbeat is lost.
    markPresenceRefreshed();
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <DropdownSection class="[&>ul]:overflow-visible">
    <div class="grid gap-0">
      <!-- NOTICE: KAPTION-INTEGRATION — agent-presence.VISIBILITY.3 -->
      <KaptionLivePresenceNotice v-if="isLivePresenceLost" />
      <DropdownItem preserve-open>
        <div class="flex-grow flex items-center gap-1">
          {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
          <!-- NOTICE: KAPTION-INTEGRATION — agent-presence.VISIBILITY.5
               The selector below shows the setting; this says what the setting
               is actually worth right now. -->
          <span
            v-if="isLivePresenceLost"
            data-testid="live-presence-badge"
            class="px-1.5 py-0.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
          >
            {{ $t('SIDEBAR.LIVE_PRESENCE.BADGE') }}
          </span>
        </div>
        <DropdownContainer>
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              class="min-w-[96px]"
              icon="i-lucide-chevron-down"
              trailing-icon
              @click="toggle"
            >
              <div class="flex gap-1 items-center flex-grow text-sm">
                <div class="p-1 flex-shrink-0">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span>{{ activeStatus.label }}</span>
              </div>
            </Button>
          </template>
          <DropdownBody class="min-w-32 z-20">
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer"
              @click="changeAvailabilityStatus(status.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
      <DropdownItem v-if="!hideAutoOfflineToggle">
        <div class="flex-grow flex items-center gap-1">
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
          <Icon
            v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
            icon="i-lucide-info"
            class="size-4 text-n-slate-10"
          />
        </div>
        <ToggleSwitch v-model="autoOfflineToggle" />
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
